/**
 * Seen History Slice
 * 
 * Redux state management for seen history.
 * Provides centralized state that can be accessed by multiple components.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { db } from '../../../firebaseConfig';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';

const CACHE_KEY = 'seen_history_cache';
const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

// Try to hydrate from localStorage
const loadCachedHistory = () => {
    try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
            const { data, timestamp } = JSON.parse(cached);
            if (Date.now() - timestamp < CACHE_TTL_MS) {
                return data;
            }
        }
    } catch { }
    return {};
};

// Fetch seen history from Firestore
export const fetchSeenHistory = createAsyncThunk(
    'seenHistory/fetch',
    async (userId, { rejectWithValue }) => {
        try {
            if (!userId) throw new Error('User ID required');

            const q = query(
                collection(db, 'user_seen_history'),
                where('userId', '==', userId),
                orderBy('seenAt', 'desc'),
                limit(500) // Last 500 seen items
            );

            const snapshot = await getDocs(q);
            const history = {};

            snapshot.docs.forEach(doc => {
                const data = doc.data();
                history[data.itemId] = {
                    seenAt: data.seenAt?.toMillis?.() || Date.now(),
                    seenBatch: data.seenBatch || 0,
                    itemType: data.itemType || 'post'
                };
            });

            // Cache to localStorage
            try {
                localStorage.setItem(CACHE_KEY, JSON.stringify({
                    data: history,
                    timestamp: Date.now()
                }));
            } catch { }

            return history;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

const initialState = {
    history: loadCachedHistory(),
    currentBatch: 0,
    loading: false,
    error: null,
    lastFetched: null
};

const seenHistorySlice = createSlice({
    name: 'seenHistory',
    initialState,
    reducers: {
        // Mark item as seen locally (instant)
        markItemSeen: (state, action) => {
            const { itemId, itemType = 'post' } = action.payload;
            state.history[itemId] = {
                seenAt: Date.now(),
                seenBatch: state.currentBatch,
                itemType
            };
        },

        // Increment seen batch (on feed refresh)
        incrementSeenBatch: (state) => {
            state.currentBatch += 1;
        },

        // Clear seen history (for testing/debugging)
        clearSeenHistory: (state) => {
            state.history = {};
            state.currentBatch = 0;
            try {
                localStorage.removeItem(CACHE_KEY);
            } catch { }
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSeenHistory.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchSeenHistory.fulfilled, (state, action) => {
                state.loading = false;
                state.history = { ...state.history, ...action.payload };
                state.lastFetched = Date.now();
            })
            .addCase(fetchSeenHistory.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    }
});

// Export actions
export const { markItemSeen, incrementSeenBatch, clearSeenHistory } = seenHistorySlice.actions;

// Selectors
export const selectSeenHistory = (state) => state.seenHistory?.history || {};
export const selectCurrentBatch = (state) => state.seenHistory?.currentBatch || 0;
export const selectIsItemSeen = (itemId) => (state) => !!state.seenHistory?.history?.[itemId];

export default seenHistorySlice.reducer;
